import { Injectable, inject } from '@angular/core';
import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { catchError, defer, map, Observable, of, retry } from 'rxjs';
import { RequestOptions, Response } from './interfaces';
import { DefaultError } from '../core/http/default-error.strategy';
import { API_URL } from '../core/inject-token';

/**
 * Injectable HTTP service that wraps Angular's HttpClient and provides
 * standardized request methods with retry and error handling strategies.
 */
@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private http = inject(HttpClient);
  private URL_PREFIX = inject(API_URL);

  /**
   * Executes a GET HTTP request.
   * @template T Response type.
   * @param url The target endpoint URL.
   * @param options Optional request options (headers, params, retries, context).
   * @returns Cold Observable emitting the response of type T.
   */
  get<T>(url: string, options?: RequestOptions): Observable<Response<T>> {
    const httpOptions = this.buildHttpOptions(options);
    return this.sendRequest(
      () => this.http.get<T>(this.URL_PREFIX + url, httpOptions),
      httpOptions,
    );
  }

  /**
   * Executes a POST HTTP request.
   * @template T Response type.
   * @template K Request body type.
   * @param url The target endpoint URL.
   * @param body The request payload.
   * @param options Optional request options (headers, params, retries, context).
   * @returns Cold Observable emitting the response of type T.
   */
  post<T, K>(url: string, body: K, options?: RequestOptions): Observable<Response<T>> {
    return this.sendRequest(
      () => this.http.post<T>(this.URL_PREFIX + url, body, this.buildHttpOptions(options)),
      this.buildHttpOptions(options),
    );
  }

  /**
   * Executes a PUT HTTP request.
   * @template T Response type.
   * @template K Request body type.
   * @param url The target endpoint URL.
   * @param body The request payload.
   * @param options Optional request options (headers, params, retries, context).
   * @returns Cold Observable emitting the response of type T.
   */
  put<T, K>(url: string, body: K, options?: RequestOptions): Observable<Response<T>> {
    return this.sendRequest(
      () => this.http.put<T>(this.URL_PREFIX + url, body, this.buildHttpOptions(options)),
      this.buildHttpOptions(options),
    );
  }

  /**
   * Executes a DELETE HTTP request.
   * @template T Response type.
   * @param url The target endpoint URL.
   * @param options Optional request options (headers, params, retries, context).
   * @returns Cold Observable emitting the response of type T.
   */
  delete<T>(url: string, options?: RequestOptions): Observable<Response<T>> {
    return this.sendRequest(
      () => this.http.delete<T>(this.URL_PREFIX + url, this.buildHttpOptions(options)),
      this.buildHttpOptions(options),
    );
  }

  /**
   * Internal method that handles the request pipeline, applying retry and error handling strategies.
   * @template T Response type.
   * @param observableFactory The HTTP request observable.
   * @param options Optional request options.
   * @returns Cold Observable emitting the response of type T.
   */
  private sendRequest<T>(
    observableFactory: () => Observable<T>,
    options: Required<RequestOptions>,
  ): Observable<Response<T>> {
    const retries = options.retries ?? 2;

    return defer(() => observableFactory())
      .pipe(retry(retries))
      .pipe(
        map(
          (data) =>
            ({
              status: 'success',
              data,
            }) as const,
        ),
        catchError((error: HttpErrorResponse, caught) => {
          options.errorStrategy.handleError(error, caught);
          return of({
            status: 'error',
            error,
          } as const);
        }),
      );
  }

  /**
   * Builds HTTP options including params, headers, and context for the request.
   * @param options Optional request options.
   * @returns An object containing Angular-compatible HTTP options.
   */
  private buildHttpOptions(options?: RequestOptions): Required<RequestOptions> {
    const sanitizedRetries = Math.max(0, options?.retries ?? 2);
    return {
      params: options?.params ?? new HttpParams(),
      headers: options?.headers ?? new HttpHeaders(),
      context: options?.context ?? new HttpContext(),
      errorStrategy: options?.errorStrategy ?? new DefaultError(),
      retries: sanitizedRetries,
    };
  }
}
